/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';

import * as statuses from 'utils/statuses';

import styles from './StatusIndicator.module.scss';

@translate()
export default class StatusIndicator extends React.Component<*> {
  renderStatusText = (statusCode: string = '') => statuses.translate(statusCode, this.props.i18n);

  renderStatus(status: string | boolean = ''): React.Element<*> | null {
    const { t, i18n } = this.props;
    const statusCode = typeof status === 'string' ? statuses.getCode(status) : status;
    const statusCodeString = String(statusCode);

    switch (statusCode) {
      case 'archived':
      case 'collection_arranged':
      case 'on_hire':
      case 'scheduled':
      case 'asset_dispatched':
      case 'in_dispatch':
        return (
          <div className={`${styles.status} ${styles.archived}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case true:
        return (
          <div className={`${styles.status} ${styles.active}`}>
            {this.renderStatusText('active')}
          </div>
        );

      case 'active':
      case 'enabled':
      case 'present':
      case 'available_for_hire':
      case 'operating_lease':
      case 'lease':
      case 'demo':
      case 'sub_rental':
      case 'arrived':
      case 'assigned':
      case 'deployed':
      case 'external_service':
      case 'internal_service':
      case 'internal_transfer':
      case 'ordered':
      case 'not_in_use':
      case 'new':
        return (
          <div className={`${styles.status} ${styles.active}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'storage':
      case 'internal_repair':
      case 'external_repair':
      case 'internal_calibration':
      case 'external_calibration':
      case 'in_test':
        return (
          <div className={`${styles.status} ${styles.storage}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'in_use':
      case 'faulty':
      case 'beyond_economic_repair':
      case 'beyond_economical_repair':
      case 'disposed':
      case 'lost':
      case 'sold':
      case 'missing_accessories':
      case 'on_rent':
      case 'on_lease':
      case 'temporary_exchange':
      case 'outside_repair':
      case 'returned_to_vendor':
      case 'in_transit':
      case 'in_test_-_new_asset':
      case 'to_be_shipped':
      case 'awaiting_parts':
      case 'deferred_repair':
      case 'scheduled_for_repair':
      case 'rejected_by_lab':
      case 'used_in_value_add':
      case 'used_in_value_delete':
      case 'created_in_a_value_delete':
      case 'internal_use':
      case 'in_repair':
      case 'written_off':
      case 'reinstated_from_write_off)':
      case 'transferred_to_inventory':
      case 'in_use_in_canada_-_frgn_asset':
      case 'in_use_in_us_-_for_frgn_asset':
      case 'on_loan_to_us':
      case 'out_on_loan':
      case 'retd_to_lending_co':
      case 'retd_from_borrower':
      case 'conversion_of_tbs _tatus':
      case 'conversion_of_null_status':
      case 'requires_check':
      case 'allocated':
      case 'not_new':
        return (
          <div className={`${styles.status} ${styles.inactive}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'available':
        return (
          <div className={`${styles.status} ${styles.available}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'unavailable':
      case 'asset_unavailable':
        return (
          <div className={`${styles.status} ${styles.unavailable}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case false:
        return (
          <div className={`${styles.status} ${styles.disabled}`}>
            {this.renderStatusText('disabled')}
          </div>
        );

      case 'unknown':
      case 'disabled':
        return (
          <div className={`${styles.status} ${styles.disabled}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'service_on_rent':
      case 'prepared':
      case 'inter_co_sale':
      case 'on_demo':
      case 'planned':
      case 'confirmed':
      case 'open':
        return (
          <div className={`${styles.status} ${styles.open}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'close':
      case 'ended':
      case 'reserved':
      case 'returned':
      case 'cancelled':
      case 'purchase_order_cancelled':
      case 'quarantine':
      case 'rejected':
      case 'return_notified':
      case 'closed':
        return (
          <div className={`${styles.status} ${styles.closed}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'mixed':
      case 'rental':
      case 'sale':
      case 'sales':
      case 'inactive':
        return (
          <div className={`${styles.status} ${styles.type}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'returnnotified':
        return (
          <div className={`${styles.status} ${styles.newStatus}`}>
            {this.renderStatusText(statusCodeString)}
          </div>
        );

      case 'serviceonrent':
      case 'usedinvalueadd':
      case 'writtenoff':
      case 'intercosale':
      case 'created':
      case 'released':
      case 'in_progress':
      case 'completed':
      case 'checked_workshop':
      case 'checked_admin':
      case 'invoiced':
      case 'rental_with_a_defined_option_to_buy':
      case 'rent_to_own':
      case 'used_equipment_sale':
      case 'new_sale':
      case 'quotation':
      case 'quotation_inspected':
      case 'picked':
      case 'outbound':
      case 'delivered':
      case 'built':
      case 'event_started':
      case 'event_ended':
      case 'strike':
      case 'inbound':
      case 'inspected':
      case 'none':
      case 'awaitingparts':
      case 'beyondeconomicalrepair':
      case 'externalservice':
      case 'internalservice':
      case 'internaltransfer':
      case 'internaluse':
      case 'missingaccessories':
      case 'ondifferedrepairs':
      case 'pocancelled':
      case 'requirescheck':
      case 'returnedtovendor':
      default:
        return <div className={`${styles.status} ${styles.newStatus}`}>{status}</div>;
    }
  }

  render(): React.Element<*> {
    const status = this.props.status;
    const className = styles['status-indicator'];
    const hasTitle = this.props.title ? styles.title : '';

    return (
      <div className={`${className} ${hasTitle}`}>
        {this.props.title && <h3>{this.props.title}</h3>}
        {this.renderStatus(status)}
      </div>
    );
  }
}
